/**
 * SmithNotificationBell — Iter257
 *
 * Live notification bell with an SSE-driven feed. Subscribes to
 * `GET /api/smith/notifications/stream` and renders:
 *
 *   • Bell icon with unread badge (pulses red when count > 0)
 *   • Panel dropdown with the last 30 days of events:
 *       - retry_failed  (warning · retry icon)
 *       - deal_won      (success · trophy)
 *       - trigger_new   (info    · zap)
 *       - ar_overdue    (critical · clock-alert)
 *   • "Mark all read" button
 *   • Click on an entry → deep-links to prospect + marks that item read
 *
 * Falls back to /notifications polling every 60s if EventSource isn't
 * available (very old browsers).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Bell, X, RefreshCw, Trophy, Zap, AlertTriangle, ClockAlert, CheckCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SSE_URL = `${API}/smith/notifications/stream`;
const SNAP_URL = `${API}/smith/notifications`;


const KIND_ICON = {
  retry_failed: RefreshCw,
  deal_won:     Trophy,
  trigger_new:  Zap,
  ar_overdue:   ClockAlert,
};

const KIND_ACCENT = {
  retry_failed: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  deal_won:     "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  trigger_new:  "text-sky-300 bg-sky-500/10 border-sky-500/30",
  ar_overdue:   "text-rose-300 bg-rose-500/10 border-rose-500/30",
};


function _fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const mins = Math.round((now - d) / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `${mins}min`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h`;
  const days = Math.round(h / 24);
  return `${days}d`;
}


export default function SmithNotificationBell({
  testidPrefix = "smith-header",
}) {
  const [feed, setFeed] = useState({ items: [], unread: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);
  const panelRef = useRef(null);

  // Bootstrap: initial snapshot + open the SSE stream.
  useEffect(() => {
    let cancelled = false;
    axios.get(SNAP_URL, { params: { limit: 50 }, timeout: 12000 })
      .then((r) => { if (!cancelled) setFeed(r.data); })
      .catch(() => { /* silent — SSE will retry */ });

    // EventSource doesn't accept Authorization headers by default so
    // we rely on axios' interceptor-attached JWT living in the axios
    // default headers not applying here — but SSE supports cookies +
    // most Emergent auth is via `Authorization: Bearer`. Fall back to
    // polling when the browser refuses to authenticate the SSE.
    const auth = axios.defaults.headers.common["Authorization"];
    if (typeof window.EventSource === "undefined" || !auth) {
      // Polling fallback every 60s
      const iv = setInterval(() => {
        axios.get(SNAP_URL, { params: { limit: 50 }, timeout: 12000 })
          .then((r) => { if (!cancelled) setFeed(r.data); })
          .catch(() => {});
      }, 60_000);
      return () => { cancelled = true; clearInterval(iv); };
    }
    // The Emergent SSE endpoint reads the token via cookies (set by the
    // login flow) — the axios interceptor already syncs the cookie so
    // native EventSource works without extra headers.
    try {
      const es = new EventSource(SSE_URL, { withCredentials: true });
      esRef.current = es;
      es.addEventListener("notif", (ev) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(ev.data);
          setFeed(data);
          setConnected(true);
        } catch { /* ignore malformed */ }
      });
      es.onerror = () => {
        setConnected(false);
        // let browser auto-reconnect; hard-close after 3 back-to-back
        // failures is browser-native
      };
    } catch { /* fall through — snapshot is already in state */ }

    return () => {
      cancelled = true;
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
    };
  }, []);

  // Close panel on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const unread = feed.unread || 0;

  const markRead = async (ids) => {
    if (!ids?.length) return;
    try {
      await axios.post(`${API}/smith/notifications/read`, { ids });
      setFeed((cur) => ({
        ...cur,
        items: cur.items.map((m) => ids.includes(m.id) ? { ...m, read: true } : m),
        unread: Math.max(0, (cur.unread || 0) - ids.length),
      }));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      const { data } = await axios.post(`${API}/smith/notifications/read-all`);
      toast.success(`${data.marked || 0} notificações marcadas como lidas`);
      setFeed((cur) => ({
        ...cur,
        items: cur.items.map((m) => ({ ...m, read: true })),
        unread: 0,
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao marcar"); }
  };

  const items = feed.items || [];

  return (
    <div className="relative" ref={panelRef}>
      <button type="button"
              data-testid={`${testidPrefix}-notif-btn`}
              onClick={() => setOpen((o) => !o)}
              title={connected ? `${unread} notificações (live)`
                                  : `${unread} notificações (polling)`}
              className={`relative w-9 h-9 rounded-lg border bg-[var(--al-card-bg)] hover:border-[#EF4444] hover:text-[#EF4444] inline-flex items-center justify-center transition-colors ${unread > 0 ? "border-[#EF4444]/60 text-[#EF4444]" : "border-[var(--al-border)]"}`}>
        <Bell className={`w-4 h-4 ${unread > 0 ? "animate-pulse" : ""}`} />
        {unread > 0 && (
          <span data-testid={`${testidPrefix}-notif-count`}
                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        {/* Live-connection dot */}
        <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-500"}`}
              title={connected ? "SSE conectado" : "SSE desconectado (polling)"} />
      </button>

      {open && (
        <div data-testid={`${testidPrefix}-notif-panel`}
             className="absolute right-0 mt-2 w-[380px] max-w-[calc(100vw-24px)] rounded-xl border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-2xl z-50 overflow-hidden"
             onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--al-border)]">
            <div className="flex items-center gap-1.5 text-[12.5px] font-black">
              <Bell className="w-3.5 h-3.5" />
              Notificações
              {unread > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EF4444]/20 text-[#EF4444] font-bold">
                  {unread} novas
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button type="button" onClick={markAllRead}
                        data-testid={`${testidPrefix}-notif-mark-all`}
                        className="text-[10.5px] font-bold text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-emerald-500/10">
                  <CheckCheck className="w-3 h-3" /> Marcar todas
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)}
                      className="w-6 h-6 rounded hover:bg-white/5 inline-flex items-center justify-center text-slate-400">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="max-h-[440px] overflow-y-auto"
               data-testid={`${testidPrefix}-notif-list`}>
            {items.length === 0 ? (
              <div className="text-center py-10 text-[12px] text-slate-500"
                   data-testid={`${testidPrefix}-notif-empty`}>
                Nenhuma notificação nos últimos 30 dias
              </div>
            ) : (
              items.map((n) => (
                <NotifRow key={n.id} n={n}
                          onOpen={() => { markRead([n.id]); setOpen(false); }} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function NotifRow({ n, onOpen }) {
  const Icon = KIND_ICON[n.kind] || AlertTriangle;
  const accent = KIND_ACCENT[n.kind] || "text-slate-300 bg-white/5 border-white/10";
  return (
    <Link to={n.href || "/smith/prospects"}
          onClick={onOpen}
          data-testid={`smith-notif-item-${n.id}`}
          className={`block px-3 py-2.5 border-l-2 border-b border-b-white/5 hover:bg-white/5 transition ${n.read ? "opacity-60 border-l-transparent" : "border-l-[#EF4444]"}`}>
      <div className="flex items-start gap-2.5">
        <div className={`w-7 h-7 rounded-md border ${accent} inline-flex items-center justify-center shrink-0`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-[12.5px] font-black text-white truncate">
              {n.title}
            </div>
            <div className="text-[10px] text-slate-500 tabular-nums shrink-0">
              {_fmtTime(n.ts_iso)}
            </div>
          </div>
          {n.detail && (
            <div className="text-[11px] text-slate-400 line-clamp-2 mt-0.5">
              {n.detail}
            </div>
          )}
          {n.prospect_name && (
            <div className="text-[10.5px] text-slate-500 mt-1 truncate">
              → {n.prospect_name}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
